import React, { Fragment, useCallback, useEffect, useReducer, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import StickyHeaderDataTable from "../shared/sticky-header-data-table";
import { useDebouncedCallback } from "use-debounce";
import { useParams } from "react-router-dom";
import moment from "moment-timezone";

import axios from "axios";
import ai from "../../helpers/axios";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Loader from "../loader";
import shortenName from "../../helpers/shorten-name";
import { formatTripDate } from "../../helpers/dates";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import Tooltip from "@mui/material/Tooltip";

const columns = [
  {
    name: "Created At",
    cell: (row) => {
      return row.createdAt ? moment(row.createdAt).format("M/D/YY - h:mm a") : "";
    },
    width: "130px",
  },
  {
    name: "Legs",
    selector: "legs",
    cell: (row) => {
      return (
        <Tooltip
          classes={{
            tooltip: "multiline-tooltip",
          }}
          title={row.legs.map((leg) => (
            `Leg ${leg.legIndex !== -1 ? leg.legIndex + 1 : ""}: ${formatTripDate(leg.departureDate)} ${leg.departureAirport?.code}-${leg.arrivalAirport?.code}`
          )).join("\n") || ""}
        >
          <div className="two-line-content-responsive">
            {row.legs.map((leg, index) => (
              <div key={index}>
                Leg {leg.legIndex !== -1 ? leg.legIndex + 1 : ""}: {formatTripDate(leg.departureDate)} {leg.departureAirport?.code}-{leg.arrivalAirport?.code}
              </div>
            ))}
          </div>
        </Tooltip>
      );
    },
    width: "220px",
  },
  {
    name: "Item",
    selector: "name",
    cell: (row) => {
      return row.name;
    },
    wrap: true,
    minWidth: "150px",
    maxWidth: "250px",
  },
  {
    name: "Quantity",
    selector: "quantity",
    width: "75px",
  },
  {
    name: "Serving Size",
    selector: "item.servings",
    cell: (row) => {
      return row.item?.servings || "";
    },
    wrap: true,
    minWidth: "120px",
    maxWidth: "180px",
  },
  {
    name: "Notes",
    selector: "notes",
    wrap: true,
    minWidth: "200px",
    cell: (row) => {
      return (
        <Tooltip
          classes={{
            tooltip: "multiline-tooltip",
          }}
          title={row.notes || ""}
        >
          <div className="two-line-content-responsive">
            {row.notes || ""}
          </div>
        </Tooltip>
      );
    },
  },
  {
    name: "Status",
    selector: "status",
    cell: (row) => {
      return row.status === "added" ? "Added" : "";
    },
    width: "60px",
  },
  {
    name: "Added by",
    selector: "addedBy",
    minWidth: "80px",
    maxWidth: "120px",
    cell: (row) => row.addedBy ? shortenName(row.addedBy.name) : "",
  },
];

const conditionalRowStyles = [
  {
    when: (row) => row.status === "added",
    style: {
      backgroundColor: "#9ce19b !important",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#247523 !important",
      },
    },
  },
  {
    when: (row) => /\bcustom\b/i.test(row.name || ""),
    style: row => (
      row.status === "added" ? {
        color: "var(--evoburgundy) !important",
        backgroundColor: "#9ce19b !important",
        "&:hover": {
          cursor: "pointer",
          backgroundColor: "#247523 !important",
        },
      } : {
        color: "var(--evoburgundy) !important",
      }
    ),
  },
];

const isRowDisabled = (row) => row.status === "added" || !row.legs?.length;

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        isLoading: true,
        selectedRows: [],
        clearSelectedRows: !state.clearSelectedRows,
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        isLoading: false,
        data: (action?.payload?.data?.data || []).map((item) => ({
          ...item,
          legs: item.legs.map(leg => ({
            ...leg,
            legIndex: action?.payload?.legs?.findIndex(l => l._id === leg._id),
          })),
        })),
        totalLength: action?.payload?.data?.countTotal,
      };
    }

    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }

    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
        currentPage: 1,
      };
    }

    case "rowSelection": {
      return {
        ...state,
        selectedRows: action.payload.selectedRows,
      };
    }

    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    default:
      break;
  }
  return state;
};

const initialState = {
  isLoading: true,
  data: [],
  totalLength: 0,
  countPerPage: 5,
  currentPage: 1,
  selectedRows: [],
  clearSelectedRows: false,
  isSaving: false,
  refreshDataToken: Date.now(),
};

const CateringRequestsTableModule = ({ tripData, readOnly }) => {
  const { id } = useParams();

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);
  const abortControllerRef = useRef(null);

  const getData = useCallback(async (params) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    dispatch({ type: "dataBeganDownloading" });

    try {
      const token = await getAccessTokenSilently().catch(loginWithRedirect);

      const { data } = await ai.auth(token).get(`/api/trips/${id}/catering-requests`, {
        signal,
        params,
      });

      dispatch({
        type: "dataDownloaded",
        payload: {
          data,
          legs: tripData?.itineraryDetails?.legs || [],
        },
      });
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error(error);
    }
  }, [getAccessTokenSilently, loginWithRedirect, id, tripData]);

  const debouncedGetData = useDebouncedCallback(
    (params) => {
      getData(params);
    },
    500,
  );

  useEffect(() => {
    debouncedGetData({
      page: state.currentPage,
      countPerPage: state.countPerPage,
    });
  }, [
    debouncedGetData,
    state.currentPage,
    state.countPerPage,
    state.refreshDataToken,
  ]);

  const handleSelection = useCallback((rowData) => {
    dispatch({
      type: "rowSelection",
      payload: {
        selectedRows: rowData.selectedRows,
        allSelected: rowData.allSelected,
      },
    });
  }, []);

  const handlePageChange = useCallback((page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  }, []);

  const handleRowsPerPageChange = useCallback((currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  }, []);

  const handleAddToItinerary = useCallback(async () => {
    if (!state.selectedRows?.length) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    const token = await getAccessTokenSilently().catch(loginWithRedirect);

    const response = await ai.auth(token)
      .post(`/api/trips/${id}/catering-requests`, {
        ids: state.selectedRows.map(({ _id }) => _id),
      })
      .catch((error) => {
        Sentry.captureException(error);
      });

    if (response?.data) {
      toast.success("Catering requests have been added to itinerary Successfully");
      dispatch({
        type: "fieldChanged",
        field: "refreshDataToken",
        value: Date.now(),
      });
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: false,
    });
  }, [getAccessTokenSilently, loginWithRedirect, state.selectedRows, id]);

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1={
          <h1 className="page-title d-flex" data-testid="catering-requests-title">
            Catering Requests

            {!readOnly && (
              <button
                className="base-button icon-button icon-button-small evo-blue ml-2"
                onClick={handleAddToItinerary}
                title="Add to Itinerary"
                disabled={!state.selectedRows?.length || state.isSaving}
              >
                <span className="material-icons">publish</span>
              </button>
            )}
          </h1>
        }
        extraClasses="compact-view"
      />

      <div
        className="server-pagination-table-container trips-list-table-container data-table-compact-view shadow-none mt-2">
        <StickyHeaderDataTable
          columns={columns}
          data={state.data}
          progressPending={state.isLoading}
          progressComponent={<Loader/>}
          persistTableHead
          highlightOnHover
          responsive
          conditionalRowStyles={conditionalRowStyles}

          onSelectedRowsChange={handleSelection}
          selectableRows={!readOnly}
          clearSelectedRows={state.clearSelectedRows}
          selectableRowDisabled={isRowDisabled}

          pagination
          paginationServer
          paginationTotalRows={state.totalLength}
          paginationPerPage={state.countPerPage}
          paginationDefaultPage={state.currentPage}
          paginationRowsPerPageOptions={[5, 10, 15]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
        />
      </div>
    </Fragment>
  );
};

export default CateringRequestsTableModule;
